import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "src/App/hooks";
import {
  Button,
  CheckBox,
  FullScreenLoading,
  StaticDropdown,
  TextInput,
} from "src/Components";
import {
  setCCOrderEditRowValues,
  setCCOrderOrderTableFetchDataFlag,
} from "src/Features/CCOrder/ccOrderSlice";
import {
  ICCOrderCriterionTypes,
  ICCOrderEditValues,
} from "src/Interfaces/ccOrder";
import { editCCOrder, getCCOrderCriterionTypes } from "src/Requests/ccOrder";
import { packetErrorHandler } from "src/Utils/requestUtils";
import {
  fallbackCCOrderCriterionTypes,
  getCCOrderCriterionOptions,
  getCCOrderCriterionTypeName,
  getDefaultCCOrderCriterionType,
} from "src/Utils/ccOrderCriterionTypes";

interface Props {
  isConditionId: boolean;
}

const EditOrder: React.FC<Props> = ({ isConditionId }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const editOrderRowValues = useAppSelector(
    (store) => store.ccOrder.editOrderRowValues,
  );

  const [criterionTypes, setCriterionTypes] =
    useState<ICCOrderCriterionTypes>(fallbackCCOrderCriterionTypes);

  const [formValues, setFormValues] = useState<ICCOrderEditValues>({
    applyToSimilar: false,
    id: editOrderRowValues?.id,
    criterion: editOrderRowValues?.target,
    criterionType: editOrderRowValues?.criterionType,
  } as ICCOrderEditValues);

  useEffect(() => {
    getCCOrderCriterionTypes()
      .then(setCriterionTypes)
      .catch(packetErrorHandler);
  }, []);

  const criterionOptions = useMemo(
    () =>
      getCCOrderCriterionOptions(
        criterionTypes,
        editOrderRowValues?.coveredCallOrderType,
      ),
    [criterionTypes, editOrderRowValues?.coveredCallOrderType],
  );

  useEffect(() => {
    if (!editOrderRowValues) return;

    setFormValues({
      applyToSimilar: false,
      criterion: editOrderRowValues.target,
      criterionType:
        editOrderRowValues.criterionType ??
        getDefaultCCOrderCriterionType(
          criterionTypes,
          editOrderRowValues.coveredCallOrderType,
        ),
      id: editOrderRowValues.id,
    } as ICCOrderEditValues);
  }, [editOrderRowValues, criterionTypes]);

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const onSendClick = () => {
    setLoading(true);
    const data = { ...formValues };
    if (isConditionId) {
      data.conditionId = editOrderRowValues!.id;
    }
    editCCOrder(data)
      .then((resp) => {
        toast.success(resp.msg);
        dispatch(setCCOrderEditRowValues(null));
        dispatch(setCCOrderOrderTableFetchDataFlag(true));
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const checkIsBtnDisabled = () => {
    if (!formValues.criterion) return true;
    return false;
  };

  const onCancelClick = () => {
    dispatch(setCCOrderEditRowValues(null));
  };

  if (!editOrderRowValues) return null;

  return (
    <div className="flex w-[760px] max-w-full flex-col items-stretch gap-4 whitespace-nowrap rounded-md border-2 border-gray-300 bg-white px-4 py-4 text-xs">
      {loading ? <FullScreenLoading /> : <></>}

      <div className="grid grid-cols-2 gap-x-12 gap-y-2 font-bold">
        {!isConditionId ? (
          <>
            <div className="flex min-h-6 flex-row items-center gap-2">
              <div>اکانت:</div>
              <div>{(editOrderRowValues as any).account}</div>
            </div>
            <div className="flex min-h-6 flex-row items-center gap-2">
              <div>فرآیند:</div>
              <div>{editOrderRowValues?.process}</div>
            </div>
          </>
        ) : (
          <>
            <div className="flex min-h-6 flex-row items-center gap-2">
              <div>فرآیند:</div>
              <div>{editOrderRowValues?.process}</div>
            </div>
            <div />
          </>
        )}

        <div className="flex min-h-6 flex-row items-center gap-2">
          <div>نماد اول:</div>
          <div
            className={`${
              editOrderRowValues?.orderSideStr?.split("_")[0]?.trim() === "خرید"
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {editOrderRowValues?.instrumentName.split("_")[0]}
          </div>
        </div>
        <div className="flex min-h-6 flex-row items-center gap-2">
          <div>جهت اول:</div>
          <div
            className={`${
              editOrderRowValues?.orderSideStr?.split("_")[0]?.trim() === "خرید"
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {editOrderRowValues?.orderSideStr?.split("_")[0] === "خرید"
              ? "خرید"
              : "فروش"}
          </div>
        </div>

        <div className="flex min-h-6 flex-row items-center gap-2">
          <div>نماد دوم:</div>
          <div
            className={`${
              editOrderRowValues?.orderSideStr?.split("_")[1]?.trim() === "خرید"
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {editOrderRowValues?.instrumentName?.split("_")[1]?.trim()}
          </div>
        </div>
        <div className="flex min-h-6 flex-row items-center gap-2">
          <div>جهت دوم:</div>
          <div
            className={`${
              editOrderRowValues?.orderSideStr?.split("_")[1]?.trim() === "خرید"
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {editOrderRowValues?.orderSideStr?.split("_")[1]?.trim()}
          </div>
        </div>

        {editOrderRowValues?.instrumentIds.length === 3 ? (
          <>
            <div className="flex min-h-6 flex-row items-center gap-2">
              <div>نماد سوم:</div>
              <div
                className={`${
                  editOrderRowValues?.orderSideStr?.split("_")[2]?.trim() ===
                  "خرید"
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {editOrderRowValues?.instrumentName?.split("_")[2]?.trim()}
              </div>
            </div>
            <div className="flex min-h-6 flex-row items-center gap-2">
              <div>جهت سوم:</div>
              <div
                className={`${
                  editOrderRowValues?.orderSideStr?.split("_")[2]?.trim() ===
                  "خرید"
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {editOrderRowValues?.orderSideStr?.split("_")[2]?.trim()}
              </div>
            </div>
          </>
        ) : (
          <></>
        )}

        {criterionOptions.length > 1 ? (
          <>
            <StaticDropdown
              dropdown={{
                name: "criterionType",
                placeholder: "نوع معیار",
                extraLabel: true,
              }}
              onChange={changeHandler}
              options={criterionOptions}
              value={formValues.criterionType}
            />
            <TextInput
              label="مقدار"
              name="criterion"
              value={formValues.criterion?.toString()}
              onChange={changeHandler}
              classname="ltr !text-right"
              extraLabel
            />
          </>
        ) : (
          <div className="col-span-2">
            <TextInput
              label={`معیار: ${getCCOrderCriterionTypeName(
                criterionTypes,
                editOrderRowValues?.coveredCallOrderType,
                formValues.criterionType,
              )}`}
              name="criterion"
              value={formValues.criterion?.toString()}
              onChange={changeHandler}
              classname="ltr !text-right"
              extraLabel
            />
          </div>
        )}

        <div className="col-span-2 flex justify-end pt-1">
          <CheckBox
            checked={formValues.applyToSimilar}
            name="applyToSimilar"
            label="همه سفارش‌های مشابه"
            onChange={changeHandler}
          />
        </div>
      </div>

      <div className="flex w-full flex-row items-center justify-between border-t border-gray-200 pt-3">
        <Button
          onClick={onCancelClick}
          text="لغو"
          variant="outlined"
          className="!w-16 !h-6 !text-xs"
        />
        <Button
          onClick={onSendClick}
          text="ارسال"
          variant="contained"
          className="!w-16 !h-6 !text-xs"
          disabled={checkIsBtnDisabled()}
        />
      </div>
    </div>
  );
};

export default EditOrder;
